import { apiDelete, apiDownload, apiGet, apiPatch, apiPost, apiPut, apiUpload } from "../core/api/httpClient.js";
import { CmtsApiError } from "../core/api/cmtsApiErrors.js";
import { apiRoutes, buildApiRoute, type ApiRouteDefinition } from "../core/api/apiPaths.js";
import { canonicalMasterTypeForGenericApi } from "../core/master-data/cmtsSupportedMasterTypes.js";
import { requireCmtsGuid } from "../core/utils/cmtsGuid.js";
import type { CrudApi, DependencyCheckResult, DropdownOption, EntityCreateRequest, EntityRecord, EntitySchema, EntityUpdateRequest, PageQuery, PagedResult, ValidationResult } from "../types/api.types.js";

type QueryValue = string | number | boolean | null | undefined;
type ApiQuery = Record<string, QueryValue>;

type CrudRouteSet = {
  readonly list: ApiRouteDefinition;
  readonly get: ApiRouteDefinition;
  readonly create: ApiRouteDefinition;
  readonly update: ApiRouteDefinition;
  readonly delete: ApiRouteDefinition;
  readonly activate?: ApiRouteDefinition | undefined;
  readonly deactivate?: ApiRouteDefinition | undefined;
  readonly validate?: ApiRouteDefinition | undefined;
  readonly schema?: ApiRouteDefinition | undefined;
  readonly dependencies?: ApiRouteDefinition | undefined;
  readonly dropdown?: ApiRouteDefinition | undefined;
};

type MasterDataCrudOptions = {
  readonly masterType: string;
};

export type BackendUnavailableDetails = {
  readonly serviceName: string;
  readonly methodName: string;
  readonly routeKey?: string | undefined;
  readonly reason: string;
};

export function createBackendUnavailableError(details: BackendUnavailableDetails): CmtsApiError {
  return new CmtsApiError({
    kind: "VALIDATION",
    code: "CMTS_BACKEND_ROUTE_UNAVAILABLE",
    status: 501,
    message: `${details.serviceName}.${details.methodName} is not available because the backend route is not present in the current route contract.`,
    correlationId: undefined,
    errors: [],
    retryable: false,
    details: {
      serviceName: details.serviceName,
      methodName: details.methodName,
      routeKey: details.routeKey,
      reason: details.reason,
      remediation: "Add the backend route to the CMTS API contract and apiRoutes.config.ts, then enable this service method.",
    },
    raw: details,
  });
}

export function toQueryParams(query?: PageQuery): ApiQuery | undefined {
  if (!query) return undefined;
  const params: ApiQuery = {};
  if (query.page !== undefined) params.page = query.page;
  if (query.pageSize !== undefined) params.pageSize = query.pageSize;
  if (query.search !== undefined && query.search.trim()) params.search = query.search.trim();
  if (query.sortBy !== undefined && query.sortBy.trim()) params.sortBy = query.sortBy.trim();
  if (query.sortDirection !== undefined) params.sortDirection = query.sortDirection;
  if (query.activeOnly !== undefined) params.activeOnly = query.activeOnly;
  if (query.filters) {
    for (const [key, value] of Object.entries(query.filters)) {
      if (value === undefined || value === null) continue;
      params[key] = value;
    }
  }
  return Object.keys(params).length ? params : undefined;
}

export function normalizePagedResult<T>(input: unknown, query?: PageQuery): PagedResult<T> {
  const fallbackPage = query?.page ?? 1;
  const fallbackPageSize = query?.pageSize ?? 25;

  // unwrap ApiResponse<T>
  if (isRecord(input) && isRecord(input.data)) {
    return normalizePagedResult<T>(input.data, query);
  }

  if (Array.isArray(input)) {
    const items = input as T[];
    return {
      items,
      page: fallbackPage,
      pageSize: fallbackPageSize,
      totalCount: items.length
    };
  }

  if (isRecord(input)) {
    const rawItems =
      input.items ??
      input.records ??
      input.rows;

    if (Array.isArray(rawItems)) {
      const items = rawItems as T[];

      return {
        items,
        page: toNumber(
          input.page ?? input.pageNumber,
          fallbackPage
        ),
        pageSize: toNumber(
          input.pageSize,
          fallbackPageSize
        ),
        totalCount: toNumber(
          input.totalCount ?? input.total,
          items.length
        )
      };
    }
  }

  throw createBackendUnavailableError({
    serviceName: "entityApiShared",
    methodName: "normalizePagedResult",
    reason:
      "Backend list response did not match a supported paged result shape.",
  });
}
export function normalizeDropdownOptions(input: unknown): DropdownOption[] {
  const array = Array.isArray(input)
    ? input
    : isRecord(input) && Array.isArray(input.items)
      ? input.items
      : isRecord(input) && Array.isArray(input.data)
        ? input.data
        : null;
  if (!array) {
    throw createBackendUnavailableError({
      serviceName: "entityApiShared",
      methodName: "normalizeDropdownOptions",
      reason: "Backend dropdown response did not contain an array in the canonical response location.",
    });
  }
  return array.map((item) => normalizeDropdownOption(item));
}

export function createStandardCrudApi<T extends EntityRecord = EntityRecord, TCreate extends EntityCreateRequest = EntityCreateRequest, TUpdate extends EntityUpdateRequest = EntityUpdateRequest>(routes: CrudRouteSet): CrudApi<T, TCreate, TUpdate> {
  const crud: CrudApi<T, TCreate, TUpdate> = {
    list: async (query, signal) => normalizePagedResult<T>(await apiGet<unknown>(buildApiRoute(routes.list, {}, { query: toQueryParams(query) }), { signal }), query),
    get: (id, signal) => apiGet<T>(buildApiRoute(routes.get, { id }), { signal }),
    create: (payload, idempotencyKey) => apiPost<T>(buildApiRoute(routes.create), payload, { idempotencyKey }),
    update: (id, payload, idempotencyKey) => apiPut<T>(buildApiRoute(routes.update, { id }), payload, { idempotencyKey }),
    softDelete: async (id, reason, rowVersion) => {
      await apiDelete<void>(buildApiRoute(routes.delete, { id }, { query: cleanQuery({ reason, rowVersion }) }));
    },
  };
  if (routes.activate) crud.activate = (id, reason) => apiPatch<T>(buildApiRoute(routes.activate as ApiRouteDefinition, { id }), cleanBody({ reason }));
  if (routes.deactivate) crud.deactivate = (id, reason) => apiPatch<T>(buildApiRoute(routes.deactivate as ApiRouteDefinition, { id }), cleanBody({ reason }));
  if (routes.validate) crud.validate = (payload) => apiPost<ValidationResult>(buildApiRoute(routes.validate as ApiRouteDefinition), payload);
  if (routes.schema) crud.schema = () => apiGet<EntitySchema>(buildApiRoute(routes.schema as ApiRouteDefinition));
  if (routes.dependencies) crud.dependencies = (id) => apiGet<DependencyCheckResult>(buildApiRoute(routes.dependencies as ApiRouteDefinition, { id }));
  if (routes.dropdown) crud.dropdown = async (query) => normalizeDropdownOptions(await apiGet<unknown>(buildApiRoute(routes.dropdown as ApiRouteDefinition, {}, { query: toQueryParams(query) })));
  return crud;
}


function stripCreateTechnicalId<TPayload extends EntityCreateRequest | EntityUpdateRequest>(payload: TPayload): Omit<TPayload, "id"> {
  const { id: _discardedTechnicalId, ...rest } = payload as TPayload & { readonly id?: unknown };
  return rest;
}

function canonicalMasterPayload<TPayload extends EntityCreateRequest | EntityUpdateRequest>(masterType: string, payload: TPayload): TPayload {
  return { ...stripCreateTechnicalId(payload), masterType, group: masterType } as unknown as TPayload;
}

function canonicalCreateMasterPayload<TPayload extends EntityCreateRequest>(masterType: string, payload: TPayload): TPayload {
  return canonicalMasterPayload(masterType, stripCreateTechnicalId(payload) as TPayload);
}

export function requireMasterCrudGuidForGenericApi(id: string, operation: string): string {
  return requireCmtsGuid(id, `Master data ${operation}`);
}

export function createMasterDataCrudApi<T extends EntityRecord = EntityRecord, TCreate extends EntityCreateRequest = EntityCreateRequest, TUpdate extends EntityUpdateRequest = EntityUpdateRequest>(options: MasterDataCrudOptions): CrudApi<T, TCreate, TUpdate> {
  const paramsFor = () => ({ masterType: canonicalMasterTypeForGenericApi(options.masterType) });
  const createPayloadFor = (payload: TCreate): TCreate => canonicalCreateMasterPayload(paramsFor().masterType, payload);
  const updatePayloadFor = (payload: TUpdate): TUpdate => canonicalMasterPayload(paramsFor().masterType, payload);
  const idFor = (id: string, operation: string): string => requireMasterCrudGuidForGenericApi(id, operation);
  return {
    list: async (query, signal) => normalizePagedResult<T>(await apiGet<unknown>(buildApiRoute(apiRoutes.adminMasterData.list, paramsFor(), { query: toQueryParams(query) }), { signal }), query),
    get: (id, signal) => apiGet<T>(buildApiRoute(apiRoutes.adminMasterData.get, { ...paramsFor(), id: idFor(id, "get") }), { signal }),
    create: (payload, idempotencyKey) => apiPost<T>(buildApiRoute(apiRoutes.adminMasterData.create, paramsFor()), createPayloadFor(payload), { idempotencyKey }),
    update: (id, payload, idempotencyKey) => apiPut<T>(buildApiRoute(apiRoutes.adminMasterData.update, { ...paramsFor(), id: idFor(id, "update") }), updatePayloadFor(payload), { idempotencyKey }),
    softDelete: async (id, reason, rowVersion) => {
      await apiDelete<void>(buildApiRoute(apiRoutes.adminMasterData.delete, { ...paramsFor(), id: idFor(id, "delete") }, { query: cleanQuery({ reason, rowVersion }) }));
    },
    activate: (id, reason) => apiPatch<T>(buildApiRoute(apiRoutes.adminMasterData.activate, { ...paramsFor(), id: idFor(id, "activate") }), cleanBody({ reason })),
    deactivate: (id, reason) => apiPatch<T>(buildApiRoute(apiRoutes.adminMasterData.deactivate, { ...paramsFor(), id: idFor(id, "deactivate") }), cleanBody({ reason })),
    validate: (payload) => apiPost<ValidationResult>(buildApiRoute(apiRoutes.adminMasterData.validate, paramsFor()), canonicalMasterPayload(paramsFor().masterType, payload)),
    schema: () => apiGet<EntitySchema>(buildApiRoute(apiRoutes.adminMasterData.schema, paramsFor())),
    dependencies: (id) => apiGet<DependencyCheckResult>(buildApiRoute(apiRoutes.adminMasterData.dependencyCheck, { ...paramsFor(), id: idFor(id, "dependency check") })),
    dropdown: async (query) => normalizeDropdownOptions(await apiGet<unknown>(buildApiRoute(apiRoutes.adminMasterData.dropdownOptions, paramsFor(), { query: toQueryParams(query) }))),
  };
}
export function cleanQuery(input: Readonly<Record<string, QueryValue>>): ApiQuery | undefined {
  const output: ApiQuery = {};
  for (const [key, value] of Object.entries(input)) {
    if (value === undefined || value === null) continue;
    if (typeof value === "string" && !value.trim()) continue;
    output[key] = value;
  }
  return Object.keys(output).length ? output : undefined;
}

export function cleanBody(input: Readonly<Record<string, QueryValue>>): Record<string, string | number | boolean> | undefined {
  const output: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(input)) {
    if (value === undefined || value === null) continue;
    if (typeof value === "string" && !value.trim()) continue;
    output[key] = value;
  }
  return Object.keys(output).length ? output : undefined;
}

export async function getPaged<T>(route: ApiRouteDefinition, query?: PageQuery, signal?: AbortSignal): Promise<PagedResult<T>> {
  return normalizePagedResult<T>(await apiGet<unknown>(buildApiRoute(route, {}, { query: toQueryParams(query) }), { signal }), query);
}

export async function getArray<T>(route: ApiRouteDefinition, query?: PageQuery, signal?: AbortSignal): Promise<T[]> {
  const response = await apiGet<unknown>(buildApiRoute(route, {}, { query: toQueryParams(query) }), { signal });
  if (Array.isArray(response)) return response as T[];
  if (isRecord(response) && Array.isArray(response.items)) return response.items as T[];
  if (isRecord(response) && Array.isArray(response.data)) return response.data as T[];
  return normalizePagedResult<T>(response, query).items.slice();
}

export { apiDelete, apiDownload, apiGet, apiPatch, apiPost, apiPut, apiUpload, buildApiRoute, apiRoutes };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function toNumber(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function normalizeDropdownOption(input: unknown): DropdownOption {
  if (!isRecord(input)) return { value: String(input), label: String(input) };
  const backendId = readString(input.id) ?? readString(input.value);
  const value = backendId ?? "";
  const label = readString(input.label) ?? readString(input.name) ?? readString(input.displayName) ?? readString(input.title) ?? value;
  const metadata = isRecord(input.metadata) ? sanitizeMetadata(input.metadata) : undefined;
  return {
    id: backendId,
    value,
    label,
    code: readString(input.code),
    isActive: typeof input.isActive === "boolean" ? input.isActive : undefined,
    metadata,
  };
}

function readString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function sanitizeMetadata(input: Record<string, unknown>): Record<string, string | number | boolean | null> {
  const output: Record<string, string | number | boolean | null> = {};
  for (const [key, value] of Object.entries(input)) {
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean" || value === null) output[key] = value;
  }
  return output;
}
